// Turns a face-verification API response into an IDENTITY log row.
//
// Both peers produce these — the recorder for the calls it makes itself, the
// client for calls the recorder delegated to it — but only the recorder ever
// POSTs them, so this builder is deliberately pure: it takes a response body
// and returns { message, metadata }, and knows nothing about fetch or PubSub.
// It lives here rather than in a component for the same reason participantStats
// does: these response keys are undocumented and actively misleading (the score
// is `score` on /verify but `accuracy` on /detect-spoof), so the exact shape
// belongs somewhere a unit test can pin it.
//
// Nothing is normalised. The raw API number goes into the sentence and the raw
// body goes into METADATA, because /verify legitimately returns values like
// 0.0016 — rescaling that to a 0-100 integer would store a bare 0 and lose the
// distinction between "barely matched" and "no score at all".

export const EXECUTED_BY = {
  RECORDER: "recorder",
  CLIENT: "client",
};

// apiName is the pipeline's own identifier (see useFaceVerification), not the
// endpoint path. Each entry pins the response keys that endpoint really returns,
// in the order they should read in the sentence.
const API_SPEC = {
  faces: { label: "Face detection", keys: ["number_of_faces"] },
  spoof: { label: "Spoof detection", keys: ["spoof_detected", "accuracy"] },
  match: { label: "Face match", keys: ["verified", "score"] },
};

// Raw values only — String() keeps 0.0016 as "0.0016" and 0.9899068176746368 at
// full precision, which is the whole point of storing the response untouched.
const formatValue = (value) => String(value);

/**
 * Build the IDENTITY log row for one answered face-verification call.
 *
 * Returns null when there is nothing truthful to record — no response body, or
 * an api name this module does not know. Callers must skip a null rather than
 * writing a placeholder row, because these rows are also the journey's record
 * of how many verification calls actually happened.
 *
 * @param {object}  args
 * @param {string}  args.apiName    "faces" | "spoof" | "match"
 * @param {object}  args.body       the parsed response body, verbatim
 * @param {string}  [args.page]     journey page the call ran on
 * @param {string}  [args.executedBy] EXECUTED_BY value; defaults to recorder
 * @param {boolean} [args.delegated] true when the recorder handed this API off
 * @returns {{ message: string, metadata: object } | null}
 */
export function buildIdentityLogEntry({
  apiName,
  body,
  page,
  executedBy,
  delegated,
} = {}) {
  const spec = API_SPEC[apiName];
  if (!spec || !body) return null;

  // A key the API left out is omitted entirely — "accuracy=undefined" would
  // read as though the endpoint returned the string "undefined".
  const pairs = spec.keys
    .filter((key) => body[key] !== undefined)
    .map((key) => `${key}=${formatValue(body[key])}`);

  const where = page ? ` on ${page}` : "";
  const returned = pairs.length ? ` returned ${pairs.join(", ")}` : " answered";
  const who = executedBy === EXECUTED_BY.CLIENT ? "client" : "recorder";
  const suffix = who === EXECUTED_BY.CLIENT && delegated ? " (delegated)" : "";

  return {
    message: `${spec.label}${where}${returned}. Executed by ${who}${suffix}.`,
    metadata: body,
  };
}
